import api from "@/api";
import Coordinate from "@/models/Coordinate";
import Image from "@/models/Image";
import setPageQueryParams from "@/helpers/setPageQueryParams";

const state = {
  isLoading: true,
  // allCoordinates is for full coordinates list that is loading when the application starts
  // is needs to show coordinates data that is not loaded for a current filter state, for example: route pins
  allCoordinates: null,
  // coordinates state is for current coordinates list, that shown on the map, gallery and the timeline
  coordinates: null,
  activeCoordinateId: null,
  activeImage: null,
  fullscreenImage: {
    coordinateId: null,
    pictureId: null
  }
}

const getters = {
  isCoordinatesLoaded: (state) => state.coordinates !== null,
  coordinatesToShow: (state, getters, rootState, rootGetters) => {
    return rootGetters['Routes/activeRoute'] && !rootGetters['Routes/activeRoute']?.isEditing
      ? rootGetters['Routes/activeRoute'].coordinates
      :  state.coordinates
  },
  coordinates: (state, getters) => {
    return getters.coordinatesToShow
      ?.filter(({ images }) => images.length > 0)
      || []
  },
  getGlobalCoordinateById: (state) => (coordinateId) => state.allCoordinates?.find(({ id }) => id === coordinateId) || null,
  getCoordinateById: (state) => (coordinateId) => state.coordinates?.find(({ id }) => id === coordinateId) || null,
  activeCoordinate: (state, getters) => getters.getCoordinateById(state.activeCoordinateId),

  images: (state, getters) => {
    return [].concat.apply([], getters.coordinates.map(({ images }) => images))
  },
  allImages: (state, getters) => {
    return [].concat.apply([], getters.coordinatesToShow?.map(({ allImages }) => allImages)) || []
  },
  getImageById: (state, getters) => (imageId) => getters.allImages.find(({ id }) => id === imageId) || null,
  imagesYears: (state, getters) => {
    return getters.allImages
      .map(({ sortValue }) => Math.trunc(sortValue))
      .filter((year) => typeof year === 'number' && isFinite(year))
  },
  minImageYear: (state, getters) => Math.min(...getters.imagesYears),
  getPeriodImagesYears: (state, getters) => (startYear, endYear) => {
    return getters.imagesYears.filter((year) => year >= startYear && year <= endYear)
  }
}

const mutations = {
  SET_IS_LOADING(state, isLoading) {
    state.isLoading = isLoading
  },
  SET_ALL_COORDINATES(state, coordinates) {
    state.allCoordinates = coordinates
  },
  SET_COORDINATES(state, coordinates) {
    state.coordinates = coordinates
  },
  SET_ACTIVE_IMAGE(state, image) {
    state.activeImage = image || null
  },
  SET_ACTIVE_COORDINATE_ID(state, coordinateId) {
    state.activeCoordinateId = coordinateId
  },
  SET_FULLSCREEN_IMAGE(state, data) {
    if (!data) {
      state.fullscreenImage.coordinateId = null
      state.fullscreenImage.pictureId = null
      return false;
    }

    const { coordinateId, pictureId } = data

    if (coordinateId) state.fullscreenImage.coordinateId = coordinateId
    if (pictureId) state.fullscreenImage.pictureId = pictureId
  }
}

const actions = {
  initCoordinates({ dispatch, commit }) {
    commit('SET_IS_LOADING', true)
    return api.getByFilter()
      .then(({ data: { data } }) => {
        dispatch('Filters/initFilters', data.tags, { root: true })
        let coordinates = data.coordinates.map((coordinate) => new Coordinate(coordinate))
        commit('SET_ALL_COORDINATES', coordinates)
        dispatch('setCoordinates', coordinates)
        dispatch('Filters/processRouteFilters', undefined, { root: true })
        return coordinates
      })
      .finally(() => {
        commit('SET_IS_LOADING', false)
      })
  },
  reloadCoordinates({ rootGetters, dispatch, commit }) {
    commit('SET_IS_LOADING', true)
    const filters = rootGetters['Filters/apiActiveFilters']
    return api.getByFilter(filters)
      .then(({ data: { data } }) => {
        let coordinates = data.coordinates.map((coordinate) => new Coordinate(coordinate))
        dispatch('Filters/processActiveFiltersData', data.tags, { root: true })
        dispatch('setCoordinates', coordinates)
        setPageQueryParams(filters)
        return coordinates
      })
      .finally(() => {
        commit('SET_IS_LOADING', false)
      })
  },
  getImageInfo({ commit }, imageId) {
    commit('SET_IS_LOADING', true)
    return api.getImage(imageId)
      .then(({ data: { data } }) => {
        let image = new Image(data, data.coordinate)
        commit('SET_ACTIVE_IMAGE', image)
        return image
      })
      .finally(() => {
        commit('SET_IS_LOADING', false)
      })
  },
  setCoordinates({ state, dispatch, commit }, coordinates) {
    if (state.activeCoordinateId !== null && coordinates.every(({ id }) => id !== state.activeCoordinateId)) {
      dispatch('setActiveCoordinateId', null)
    }
    commit('SET_COORDINATES', coordinates.sort((a, b) => {
      if (a.streetNameLowerCase > b.streetNameLowerCase) return 1;
      if (a.streetNameLowerCase < b.streetNameLowerCase) return -1;
      if (a.houseNumberNumeric > b.houseNumberNumeric) return 1;
      if (a.houseNumberNumeric < b.houseNumberNumeric) return -1;
      if (a.houseNumberLowerCase > b.houseNumberLowerCase) return 1;
      if (a.houseNumberLowerCase < b.houseNumberLowerCase) return -1;
      return 0;
    }))
  },
  setActiveCoordinateId({ commit }, coordinateId) {
    commit('SET_ACTIVE_COORDINATE_ID', coordinateId)
  },
  setFullscreenImage({ commit }, data) {
    commit('SET_FULLSCREEN_IMAGE', data)
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
